import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createApiClient } from '../lib/apiClient';
import { notifySuccess } from "../lib/notifications";
import { FiUserPlus, FiSearch, FiEdit2, FiX, FiSave, FiRefreshCw, FiTrash2 } from "react-icons/fi";
import "./usuarios.css";

/** ========= AXIOS INSTANCIA (usa tu server local) ========= */
const api = createApiClient({
  baseURL: process.env.REACT_APP_API_BASE_URL, // <— tu server
  headers: { "Content-Type": "application/json" },
});

/** ========= ENDPOINTS ========= */
const API = {
  LISTAR: "/api/usuarios/listar",
  CARGAR: "/api/usuarios/cargar",
  EDITAR: (usuario) => `/api/usuarios/editar/${encodeURIComponent(usuario)}`,
  ELIMINAR_ID: (id) => `/api/usuarios/id/${id}`,
  ELIMINAR_USUARIO: (usuario) => `/api/usuarios/eliminar/${encodeURIComponent(usuario)}`,
};

/** ========= ROLES DISPONIBLES =========
 *  (guardamos el texto tal cual, y para CSS usamos un slug)
 */
const ROLES = [
  "Visualizador Logistica",
  "Visualizador Finanzas",
  "Visualizador Total",
  "Administrador",
];

const emptyForm = {
  nombre: "", apellido: "", usuario: "", contraseña: "",
  dni: "", celular: "", email: "", legajo: "", rol: "",
  ciudad: "", domicilio: "",
  horario: { entrada: "", salida: "" },
  obraSocial: "", telefonoEmergencia: "",
  formaPago: "",
  datosPago: { CBU_CVU: "", alias: "", nombreTitular: "", numeroCuenta: "" },
  CUIT_CUIL: ""
};

/* ===== Portal robusto con contenedor dedicado ===== */
function ModalContainer({ children }) {
  const hostRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  if (!hostRef.current) {
    hostRef.current = document.createElement("div");
    hostRef.current.id = "modal-root";
  }

  useEffect(() => {
    document.body.appendChild(hostRef.current);
    setMounted(true);
    return () => {
      if (hostRef.current.parentNode) hostRef.current.parentNode.removeChild(hostRef.current);
    };
  }, []);

  return mounted ? createPortal(children, hostRef.current) : children;
}

export default function GestorUsuariosOperaciones() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false); // drawer lateral
  const [mode, setMode] = useState("crear"); // crear | editar
  const [form, setForm] = useState(emptyForm);
  const [editKey, setEditKey] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 8;

  /** ========= CRUD ========= */
  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(API.LISTAR);
      setUsuarios(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("GET /listar:", e?.response || e);
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  const crearUsuario = () => api.post(API.CARGAR, form);
  const editarUsuario = () => api.put(API.EDITAR(editKey), form);
  const eliminarUsuario = (u) => {
    if (!u?._id && !u?.usuario) return Promise.resolve();
    const key = u?._id ? API.ELIMINAR_ID(u._id) : API.ELIMINAR_USUARIO(u.usuario);
    return api.delete(key);
  };

  useEffect(() => { fetchUsuarios(); }, []);

  // cerrar drawer con ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setDrawerOpen(false);
    if (drawerOpen) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  /** ========= Filtro + paginado ========= */
  const filtrados = useMemo(() => {
    const k = q.trim().toLowerCase();
    if (!k) return usuarios;
    return usuarios.filter(u =>
      [u?.nombre, u?.apellido, u?.usuario, u?.email, u?.rol, u?.dni, u?.legajo, u?.ciudad]
        .some(v => String(v ?? "").toLowerCase().includes(k))
    );
  }, [q, usuarios]);

  const totalPages = Math.max(1, Math.ceil(filtrados.length / pageSize));
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtrados.slice(start, start + pageSize);
  }, [filtrados, page]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  /** ========= Drawer ========= */
  const openCrear = () => {
    setMode("crear");
    setForm(emptyForm);
    setEditKey(null);
    setDrawerOpen(true);
  };
  const openEditar = (u) => {
    setMode("editar");
    setEditKey(u.usuario);
    setForm({
      ...emptyForm,
      ...u,
      contraseña: "",
      horario: { entrada: u?.horario?.entrada || "", salida: u?.horario?.salida || "" },
      datosPago: {
        CBU_CVU: u?.datosPago?.CBU_CVU || "",
        alias: u?.datosPago?.alias || "",
        nombreTitular: u?.datosPago?.nombreTitular || "",
        numeroCuenta: u?.datosPago?.numeroCuenta || ""
      }
    });
    setDrawerOpen(true);
  };
  const closeDrawer = () => setDrawerOpen(false);

  /** ========= Form ========= */
  const onChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("horario.")) {
      const k = name.split(".")[1];
      setForm(f => ({ ...f, horario: { ...f.horario, [k]: value } }));
    } else if (name.startsWith("datosPago.")) {
      const k = name.split(".")[1];
      setForm(f => ({ ...f, datosPago: { ...f.datosPago, [k]: value } }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const validar = () => {
    const reqCreate = ["nombre", "apellido", "usuario", "contraseña"];
    for (const k of (mode === "crear" ? reqCreate : ["nombre","apellido","usuario"])) {
      if (!String(form[k] || "").trim()) return `Falta completar: ${k}`;
    }
    if (form.email && !/.+@.+\..+/.test(form.email)) return "Email inválido";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validar();
    if (err) return alert(err);
    try {
      setLoading(true);
      if (mode === "crear") await crearUsuario();
      else await editarUsuario();
      notifySuccess(mode === "crear" ? "Usuario creado correctamente." : "Usuario actualizado correctamente.");
      closeDrawer();
      await fetchUsuarios();
    } catch (ex) {
      console.error("Guardar usuario:", ex?.response || ex);
      alert("No pude guardar el usuario (ver consola).");
    } finally {
      setLoading(false);
    }
  };

  const onDeleteUsuario = async (u) => {
    const name = u?.usuario || "este usuario";
    if (!window.confirm(`Eliminar usuario ${name}?`)) return;
    try {
      setLoading(true);
      await eliminarUsuario(u);
      notifySuccess("Usuario eliminado correctamente.");
      await fetchUsuarios();
    } catch (err) {
      console.error("DELETE usuario", err?.response || err);
      alert("No se pudo eliminar el usuario");
    } finally {
      setLoading(false);
    }
  };

  // util para clase CSS del role-pill
  const roleToClass = (v) => String(v || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-");

  return (
    <div className="users-wrap">
      {/* Header */}
      <div className="users-header">
        <div className="title-stack">
          <h1>Gestor de Usuarios</h1>
          <p className="subtitle">Alta, edición y consulta de usuarios del sistema</p>
        </div>

        <div className="toolbar">
          <div className="input-search">
            <FiSearch />
            <input
              placeholder="Buscar por nombre, usuario, email, rol..."
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
            />
          </div>
          <button type="button" className="btn ghost" onClick={fetchUsuarios} title="Refrescar">
            <FiRefreshCw /> Refrescar
          </button>
          <button type="button" className="btn solid" onClick={openCrear}>
            <FiUserPlus /> Agregar usuario
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="panel table-panel">
        <div className="table-responsive">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Usuario</th><th>Nombre</th><th>Rol</th><th>Email</th>
                <th>DNI</th><th>Legajo</th><th>Ciudad</th><th>Horario</th>
                <th style={{width:110}}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {!loading && pageData.length === 0 && (<tr><td colSpan={9} className="empty">Sin resultados</td></tr>)}
              {loading && (<tr><td colSpan={9} className="empty">Cargando...</td></tr>)}
              {pageData.map(u => (
                <tr key={u._id || u.usuario}>
                  <td><span className="mono">{u.usuario}</span></td>
                  <td>{u.apellido}, {u.nombre}</td>
                  <td>
                    <span className={`role-pill ${roleToClass(u.rol)}`}>
                      {u.rol || "—"}
                    </span>
                  </td>
                  <td>{u.email || "—"}</td>
                  <td>{u.dni || "—"}</td>
                  <td>{u.legajo || "—"}</td>
                  <td>{u.ciudad || "—"}</td>
                  <td>{u?.horario?.entrada || "—"} - {u?.horario?.salida || "—"}</td>
                  <td>
                    <button type="button" className="btn icon" onClick={() => openEditar(u)} title="Editar">
                      <FiEdit2 />
                    </button>
                    <button
                      type="button"
                      className="btn icon"
                      title="Eliminar"
                      onClick={() => onDeleteUsuario(u)}
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        <div className="pager">
          <button type="button" className="btn ghost" disabled={page<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}>Anterior</button>
          <span className="pager-info">Página {page} / {totalPages}</span>
          <button type="button" className="btn ghost" disabled={page>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>Siguiente</button>
        </div>
      </div>

      {/* Drawer derecho */}
      {drawerOpen && (
        <ModalContainer>
          <div className="drawer-overlay show" onClick={closeDrawer} aria-hidden />
          <aside
            className={`drawer ${drawerOpen ? 'open' : ''} ${mode === 'crear' ? 'create' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-label={mode === 'crear' ? 'Agregar usuario' : 'Editar usuario'}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="drawer-header">
              <h3>{mode === "crear" ? "Agregar usuario" : "Editar usuario"}</h3>
              <button type="button" className="btn icon ghost" onClick={closeDrawer}><FiX /></button>
            </div>

            <form className="drawer-body" onSubmit={onSubmit}>
              <div className="form-grid drawer-grid">
                <div className="field"><label>Nombre *</label><input name="nombre" value={form.nombre} onChange={onChange} /></div>
                <div className="field"><label>Apellido *</label><input name="apellido" value={form.apellido} onChange={onChange} /></div>
                <div className="field"><label>Usuario *</label><input name="usuario" value={form.usuario} onChange={onChange} /></div>
                <div className="field">
                  <label>{mode === "crear" ? "Contrasena *" : "Nueva contrasena"}</label>
                  <input
                    name="contraseña"
                    type="password"
                    value={form.contraseña}
                    onChange={onChange}
                    placeholder={mode === "crear" ? "" : "Dejar en blanco para mantener la actual"}
                    autoComplete="new-password"
                  />
                </div>
                <div className="field"><label>DNI</label><input name="dni" value={form.dni} onChange={onChange} /></div>
                <div className="field">
                  <label>Celular</label>
                  <input
                    name="celular"
                    type="tel"
                    inputMode="tel"
                    value={form.celular}
                    onChange={onChange}
                    placeholder="Ej: 3412523117 o +5493412523117"
                  />
                  <span className="muted">Se usara para el codigo de acceso por WhatsApp.</span>
                </div>
                <div className="field"><label>Email</label><input name="email" type="email" value={form.email} onChange={onChange} /></div>
                <div className="field"><label>Legajo</label><input name="legajo" value={form.legajo} onChange={onChange} /></div>

                {/* CAMBIO: Select de Rol */}
                <div className="field">
                  <label>Rol</label>
                  <select
                    name="rol"
                    value={form.rol}
                    onChange={onChange}
                    style={{
                      background: "var(--panel-strong)", color: "var(--text)",
                      border: "1px solid var(--stroke)", borderRadius: 12,
                      padding: "10px 12px", outline: "none"
                    }}
                  >
                    <option value="">Seleccioná...</option>
                    {ROLES.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                <div className="field"><label>Ciudad</label><input name="ciudad" value={form.ciudad} onChange={onChange} /></div>
                <div className="field"><label>Domicilio</label><input name="domicilio" value={form.domicilio} onChange={onChange} /></div>
                <div className="field"><label>Entrada</label><input name="horario.entrada" value={form.horario.entrada} onChange={onChange} placeholder="08:00" /></div>
                <div className="field"><label>Salida</label><input name="horario.salida" value={form.horario.salida} onChange={onChange} placeholder="17:00" /></div>
                <div className="field"><label>Obra Social</label><input name="obraSocial" value={form.obraSocial} onChange={onChange} /></div>
                <div className="field"><label>Tel. Emergencia</label><input name="telefonoEmergencia" value={form.telefonoEmergencia} onChange={onChange} /></div>

                <div className="field"><label>Forma de pago</label><input name="formaPago" value={form.formaPago} onChange={onChange} placeholder="CBU/CVU, Cuenta, etc." /></div>
                <div className="field"><label>CBU / CVU</label><input name="datosPago.CBU_CVU" value={form.datosPago.CBU_CVU} onChange={onChange} /></div>
                <div className="field"><label>Alias</label><input name="datosPago.alias" value={form.datosPago.alias} onChange={onChange} /></div>
                <div className="field"><label>Nombre titular</label><input name="datosPago.nombreTitular" value={form.datosPago.nombreTitular} onChange={onChange} /></div>
                <div className="field"><label>Número de cuenta</label><input name="datosPago.numeroCuenta" value={form.datosPago.numeroCuenta} onChange={onChange} /></div>
                <div className="field"><label>CUIT / CUIL</label><input name="CUIT_CUIL" value={form.CUIT_CUIL} onChange={onChange} /></div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn ghost" onClick={closeDrawer}><FiX /> Cancelar</button>
                <button type="submit" className="btn solid"><FiSave /> Guardar</button>
              </div>
            </form>
          </aside>
        </ModalContainer>
      )}
    </div>
  );
}
